import {
  Transaction,
  Holding,
  HoldingDisplay,
  PriceMap,
  PortfolioStats,
  PortfolioSnapshot,
  COIN_BY_ID,
} from '../types';
import { hashColor } from './colors';
import { IDENTITY_RATES } from './displayRates';
import type { DisplayRateTable } from './displayRates';
import { todayIso } from './fxRates';
import { isMetal, METAL_DISPLAY } from './metals';
import { getAssetType } from './stocks';

/** A BUY/SELL trading fee in USD, zero when absent. */
export function txnFeeUsd(tx: Transaction): number {
  return tx.feeUsd && tx.feeUsd > 0 ? tx.feeUsd : 0;
}

/**
 * A transaction's cash total in USD — money out of pocket on a BUY, money in on
 * a SELL, and the network fee on a TRANSFER.
 *
 * Shared by every row that displays a total so they cannot drift apart, and
 * signed the same direction the fee moves the taxable gain: a buy fee makes the
 * position cost more, a sell fee makes the sale bring in less.
 */
export function txnTotalUsd(tx: Transaction): number {
  if (tx.type === 'TRANSFER') return tx.networkFee ?? 0;
  const gross = tx.amount * tx.price;
  return tx.type === 'BUY' ? gross + txnFeeUsd(tx) : gross - txnFeeUsd(tx);
}

/**
 * ROUNDING: this module deliberately does NOT round.
 *
 * The CGT engine rounds every figure to the cent, per transaction, because those
 * figures get filed and must reconcile against each other exactly. Portfolio
 * figures are indicative and recomputed on every price tick — rounding them per
 * transaction would make totals visibly jitter as prices move, for no benefit,
 * since nothing downstream has to reconcile to the cent. Full precision is kept
 * here and rounding happens once, at format time.
 *
 * The divergence between the two modules is intentional. See the matching note in
 * cgtCalculator.ts before "harmonising" them.
 */
/** Running cost basis for one coin, in USD and in the display currency. */
export interface CoinCostBasis {
  coinId: string;
  symbol: string;
  name: string;
  amount: number;
  totalCost: number;
  /** Class A: the same cost, each transaction converted at its OWN date. */
  totalCostDisplay: number;
}

/**
 * Walk the transactions into a per-coin cost basis.
 *
 * Factored out of `computeHoldings` so the tax planner can ask for a cost basis in
 * AUD — which it needs, and which is not the same as the user's display currency —
 * without a second copy of the average-cost algorithm. Two copies of this walk
 * would be two things to keep in step, and the SELL branch is exactly where a
 * divergence would be hardest to notice.
 */
export function costBasisByCoin(
  transactions: Transaction[],
  displayRates: DisplayRateTable = IDENTITY_RATES
): Record<string, CoinCostBasis> {
  const map: Record<string, CoinCostBasis> = {};

  for (const tx of transactions) {
    if (!map[tx.coinId]) {
      map[tx.coinId] = {
        coinId: tx.coinId,
        symbol: tx.symbol,
        name: tx.name,
        amount: 0,
        totalCost: 0,
        totalCostDisplay: 0,
      };
    }
    const entry = map[tx.coinId];
    // One lookup per transaction, memoised by date inside the table.
    const perUsd = displayRates.perUsdOn(tx.date);
    // Trading fee in USD, zero when absent — which is every row created before
    // fees existed, so their cost basis is unchanged to the last decimal.
    const feeUsd = tx.feeUsd && tx.feeUsd > 0 ? tx.feeUsd : 0;

    if (tx.type === 'BUY') {
      // A buy fee is part of what the position cost, so it joins cost basis and
      // raises the average buy price — matching how the CGT engine builds a parcel.
      const costUsd = tx.amount * tx.price + feeUsd;
      entry.totalCost += costUsd;
      // Converted at THIS transaction's date, which is the whole point: what the
      // purchase actually cost in the user's currency at the time.
      entry.totalCostDisplay += costUsd * perUsd;
      entry.amount += tx.amount;
    } else if (tx.type === 'TRANSFER') {
      // Transfers between user's own wallets are net-zero on amount.
      // Optional network fee adds to cost basis without changing coin count.
      if (tx.networkFee && tx.networkFee > 0) {
        entry.totalCost += tx.networkFee;
        entry.totalCostDisplay += tx.networkFee * perUsd;
      }
    } else {
      // SELL: reduce proportionally. Both running totals shrink by the same
      // FRACTION, so each keeps the mix of historical rates that built it — which
      // is why the display total cannot be recovered by scaling the USD one.
      const avgCost = entry.amount > 0 ? entry.totalCost / entry.amount : 0;
      const avgCostDisplay = entry.amount > 0 ? entry.totalCostDisplay / entry.amount : 0;
      entry.amount = Math.max(0, entry.amount - tx.amount);
      entry.totalCost = Math.max(0, entry.amount * avgCost);
      entry.totalCostDisplay = Math.max(0, entry.amount * avgCostDisplay);
      // A sell fee is a cost of disposing, not of holding, so it must NOT land in
      // the remaining position's cost basis — it belongs to the realised result,
      // which is what `coinTxnStats` computes. Applying it here would inflate the
      // avg buy price of coins the user still holds.
    }
  }

  return map;
}

export function computeHoldings(
  transactions: Transaction[],
  prices: PriceMap,
  /**
   * Converts USD into the display currency at each transaction's OWN date.
   * Defaults to the identity table, so a caller that omits it gets display
   * figures identical to the USD ones — which is what keeps every existing
   * caller and test unchanged.
   */
  displayRates: DisplayRateTable = IDENTITY_RATES
): Holding[] {
  const map = costBasisByCoin(transactions, displayRates);

  const holdingsList = Object.values(map).filter((h) => h.amount > 0.000001);
  const totalValue = holdingsList.reduce((sum, h) => {
    const price = prices[h.coinId]?.usd ?? 0;
    return sum + h.amount * price;
  }, 0);

  // Class B — what things are worth NOW, so today's rate is the only meaningful
  // one. Read once: it is the same for every holding.
  const spotPerUsd = displayRates.perUsdOn(todayIso());

  return holdingsList.map((h) => {
    const currentPrice = prices[h.coinId]?.usd ?? 0;
    const totalValue_ = h.amount * currentPrice;
    const pnl = totalValue_ - h.totalCost;
    const avgBuyPrice = h.amount > 0 ? h.totalCost / h.amount : 0;
    const coinInfo = COIN_BY_ID[h.coinId];

    // Class B at today's rate, class A already accumulated per date above, and
    // class C as the difference — which therefore carries the FX movement between
    // when the position was bought and now.
    const totalValueDisplay = totalValue_ * spotPerUsd;
    const pnlDisplay = totalValueDisplay - h.totalCostDisplay;
    const display: HoldingDisplay = {
      totalCost: h.totalCostDisplay,
      avgBuyPrice: h.amount > 0 ? h.totalCostDisplay / h.amount : 0,
      totalValue: totalValueDisplay,
      currentPrice: currentPrice * spotPerUsd,
      pnl: pnlDisplay,
      pnlPercent: h.totalCostDisplay > 0 ? (pnlDisplay / h.totalCostDisplay) * 100 : 0,
      fxEstimated: displayRates.hasEstimates(),
    };

    return {
      coinId: h.coinId,
      symbol: h.symbol,
      name: h.name,
      color: isMetal(h.coinId)
        ? (METAL_DISPLAY[h.coinId]?.color ?? hashColor(h.coinId))
        : (coinInfo?.color ?? hashColor(h.coinId)),
      assetType: getAssetType(h.coinId),
      amount: h.amount,
      avgBuyPrice,
      currentPrice,
      totalValue: totalValue_,
      totalCost: h.totalCost,
      pnl,
      pnlPercent: h.totalCost > 0 ? (pnl / h.totalCost) * 100 : 0,
      allocation: totalValue > 0 ? (totalValue_ / totalValue) * 100 : 0,
      change24h: prices[h.coinId]?.usd_24h_change ?? 0,
      display,
    } as Holding;
  });
}

export function computePortfolioStats(holdings: Holding[]): PortfolioStats {
  const totalValue = holdings.reduce((s, h) => s + h.totalValue, 0);
  const totalCost = holdings.reduce((s, h) => s + h.totalCost, 0);
  const totalPnl = totalValue - totalCost;
  const totalPnlPercent = totalCost > 0 ? (totalPnl / totalCost) * 100 : 0;

  // 24h change: weighted by current value
  const change24hValue = holdings.reduce((s, h) => {
    const prevValue = h.totalValue / (1 + h.change24h / 100);
    return s + (h.totalValue - prevValue);
  }, 0);
  const prevTotal = totalValue - change24hValue;
  const change24h = prevTotal > 0 ? (change24hValue / prevTotal) * 100 : 0;

  // Display totals are summed from the per-holding display figures rather than
  // scaled from the USD totals — the per-holding costs each carry their own mix of
  // historical rates, and no single multiplier can reproduce that.
  const dTotalValue = holdings.reduce((s, h) => s + h.display.totalValue, 0);
  const dTotalCost = holdings.reduce((s, h) => s + h.display.totalCost, 0);
  const dTotalPnl = dTotalValue - dTotalCost;
  const dChange24hValue = holdings.reduce((s, h) => {
    const prevValue = h.display.totalValue / (1 + h.change24h / 100);
    return s + (h.display.totalValue - prevValue);
  }, 0);

  return {
    totalValue,
    totalCost,
    totalPnl,
    totalPnlPercent,
    change24h,
    change24hValue,
    assetCount: holdings.length,
    display: {
      totalValue: dTotalValue,
      totalCost: dTotalCost,
      totalPnl: dTotalPnl,
      totalPnlPercent: dTotalCost > 0 ? (dTotalPnl / dTotalCost) * 100 : 0,
      change24hValue: dChange24hValue,
      fxEstimated: holdings.some((h) => h.display.fxEstimated),
    },
  };
}

export function generateInsights(holdings: Holding[], stats: PortfolioStats): string[] {
  const insights: string[] = [];
  if (holdings.length === 0) {
    insights.push('Add your first transaction to start tracking your portfolio.');
    return insights;
  }

  const sorted = [...holdings].sort((a, b) => b.totalValue - a.totalValue);
  const top = sorted[0];

  // Concentration
  if (top.allocation > 60) {
    insights.push(
      `Your portfolio is heavily concentrated in ${top.symbol} (${top.allocation.toFixed(0)}%). Consider diversifying to reduce risk.`
    );
  }

  // Top 2 concentration
  if (sorted.length >= 2) {
    const top2 = sorted[0].allocation + sorted[1].allocation;
    if (top2 > 70) {
      insights.push(
        `${sorted[0].symbol} + ${sorted[1].symbol} make up ${top2.toFixed(0)}% of your portfolio.`
      );
    }
  }

  // Best performer
  const bestPnl = [...holdings].sort((a, b) => b.pnlPercent - a.pnlPercent)[0];
  if (bestPnl.pnlPercent > 10) {
    insights.push(
      `${bestPnl.symbol} is your best performer, up ${bestPnl.pnlPercent.toFixed(1)}% from your avg buy price.`
    );
  }

  // Worst performer
  const worstPnl = [...holdings].sort((a, b) => a.pnlPercent - b.pnlPercent)[0];
  if (worstPnl.pnlPercent < -5) {
    insights.push(
      `${worstPnl.symbol} is down ${Math.abs(worstPnl.pnlPercent).toFixed(1)}% from your cost basis. Watch closely.`
    );
  }

  // Overall P&L
  if (stats.totalPnlPercent > 20) {
    insights.push(
      `Great job! Your portfolio is up ${stats.totalPnlPercent.toFixed(1)}% overall.`
    );
  } else if (stats.totalPnlPercent < -10) {
    insights.push(
      `Your portfolio is down ${Math.abs(stats.totalPnlPercent).toFixed(1)}% overall. Stay patient, markets recover.`
    );
  }

  // 24h change
  if (Math.abs(stats.change24h) > 5) {
    const dir = stats.change24h > 0 ? 'gained' : 'lost';
    insights.push(
      `Your portfolio ${dir} ${Math.abs(stats.change24h).toFixed(1)}% in the last 24 hours.`
    );
  }

  // Diversification praise
  if (holdings.length >= 5 && top.allocation < 40) {
    insights.push(`Well diversified across ${holdings.length} assets with no single coin above 40%.`);
  }

  return insights.slice(0, 5);
}

export function buildPortfolioHistory(
  transactions: Transaction[],
  prices: PriceMap,
  savedSnapshots: PortfolioSnapshot[]
): PortfolioSnapshot[] {
  if (transactions.length === 0) return [];

  // Build synthetic history from transaction dates using current prices
  const sortedTx = [...transactions].sort((a, b) => a.timestamp - b.timestamp);
  const dateSet = new Set<string>();
  sortedTx.forEach((tx) => dateSet.add(tx.date));

  const syntheticPoints: PortfolioSnapshot[] = [];
  const txByDate = sortedTx;

  for (const date of Array.from(dateSet).sort()) {
    const txUpToDate = txByDate.filter((tx) => tx.date <= date);
    const holdings = computeHoldings(txUpToDate, prices);
    const value = holdings.reduce((s, h) => s + h.totalValue, 0);
    syntheticPoints.push({ date, value });
  }

  // Merge with saved snapshots (real values), deduplicate by date
  const allPoints = new Map<string, number>();
  for (const p of syntheticPoints) allPoints.set(p.date, p.value);
  // Real snapshots override synthetic ones for the same date
  const firstTxDate = sortedTx[0].date;
  for (const p of savedSnapshots) {
    // Only use snapshots that are from after first transaction
    if (p.date >= firstTxDate) {
      allPoints.set(p.date, p.value);
    }
  }

  return Array.from(allPoints.entries())
    .map(([date, value]) => ({ date, value }))
    .sort((a, b) => a.date.localeCompare(b.date));
}
